import { Observable } from "../../events";
import { Cartesian2 } from "../../geometry";
import { TouchGestureType } from "./map.inputs.interfaces.touch";
const GestureDetectionSettings = {
    [TouchGestureType.Rotate]: { threshold: 0.5 },
    [TouchGestureType.Pinch]: { threshold: 1 },
    [TouchGestureType.Drag]: { threshold: 1 },
    [TouchGestureType.Tap]: { threshold: 0 },
    [TouchGestureType.LongPress]: { threshold: 0 },
    [TouchGestureType.Swipe]: { threshold: 0 },
};
export class PointerToGestureController {
    constructor(source) {
        this._pointers = new Map();
        this._startDistance = 0;
        this._startAngle = 0;
        this._startCenter = Cartesian2.Zero();
        this._moveCount = 0;
        this._firstMoveCountThreshold = 5;
        this._onStart = (e) => {
            if (e.pointerType !== "touch") {
                return;
            }
            const x = this._getClientX(e);
            const y = this._getClientY(e);
            const state = {
                id: e.pointerId,
                startX: x,
                startY: y,
                lastX: x,
                lastY: y,
                x: x,
                y: y,
                startTime: performance.now(),
            };
            this._pointers.set(e.pointerId, state);
            if (this._pointers.size === 2) {
                const [a, b] = Array.from(this._pointers.values());
                this._startDistance = this._distance(a, b);
                this._startAngle = this._angle(a, b);
                this._startCenter = this._midpoint(a, b);
                this._moveCount = 0;
            }
        };
        this._onMove = (e) => {
            if (e.pointerType !== "touch") {
                return;
            }
            const state = this._pointers.get(e.pointerId);
            if (state) {
                state.x = this._getClientX(e);
                state.y = this._getClientY(e);
                switch (this._pointers.size) {
                    case 1: {
                        const gesture = {
                            type: TouchGestureType.Drag,
                            timestamp: performance.now(),
                            duration: performance.now() - state.startTime,
                            points: [{ x: state.x, y: state.y }],
                            deltaX: state.x - state.lastX,
                            deltaY: state.y - state.lastY,
                        };
                        this.onTouchObservable.notifyObservers(gesture);
                        state.lastX = state.x;
                        state.lastY = state.y;
                        break;
                    }
                    case 2: {
                        this._moveCount++;
                        if (this._moveCount < this._firstMoveCountThreshold) {
                            return;
                        }
                        const [a, b] = Array.from(this._pointers.values());
                        const center = this._midpoint(a, b);
                        const angle = this._angle(a, b);
                        const distance = this._distance(a, b);
                        const deltaAngle = angle - this._startAngle;
                        const deltaDistance = distance - this._startDistance;
                        const deltaCenter = this._distance(center, this._startCenter);
                        const types = this._detectActiveTypes(deltaAngle, deltaDistance, deltaCenter);
                        for (const type of types) {
                            switch (type) {
                                case TouchGestureType.Rotate: {
                                    const gesture = {
                                        type: TouchGestureType.Rotate,
                                        timestamp: performance.now(),
                                        duration: 0,
                                        points: [
                                            { x: a.x, y: a.y },
                                            { x: b.x, y: b.y },
                                        ],
                                        center: center,
                                        angle: deltaAngle,
                                    };
                                    this._startAngle = angle;
                                    this.onTouchObservable.notifyObservers(gesture);
                                    break;
                                }
                                case TouchGestureType.Pinch: {
                                    const gesture = {
                                        type: TouchGestureType.Pinch,
                                        timestamp: performance.now(),
                                        duration: 0,
                                        points: [
                                            { x: a.x, y: a.y },
                                            { x: b.x, y: b.y },
                                        ],
                                        center: center,
                                        scale: deltaDistance,
                                    };
                                    this._startDistance = distance;
                                    this.onTouchObservable.notifyObservers(gesture);
                                    break;
                                }
                                case TouchGestureType.Drag: {
                                    const gesture = {
                                        type: TouchGestureType.Drag,
                                        timestamp: performance.now(),
                                        duration: 0,
                                        points: [
                                            { x: a.x, y: a.y },
                                            { x: b.x, y: b.y },
                                        ],
                                        deltaX: center.x - this._startCenter.x,
                                        deltaY: center.y - this._startCenter.y,
                                        startPosition: this._startCenter,
                                    };
                                    this._startCenter = { x: center.x, y: center.y };
                                    this.onTouchObservable.notifyObservers(gesture);
                                    break;
                                }
                            }
                        }
                        break;
                    }
                }
            }
        };
        this._onEnd = (e) => {
            if (e.pointerType !== "touch") {
                return;
            }
            this._pointers.delete(e.pointerId);
        };
        this._source = source;
    }
    dispose() {
        this._detachSource(this._source);
        this._clearObservable;
    }
    get onTouchObservable() {
        if (!this._onTouchObservable) {
            this._onTouchObservable = new Observable();
            this._attachSource(this._source);
        }
        return this._onTouchObservable;
    }
    get source() {
        return this._source;
    }
    _getClientX(e) {
        return e.clientX;
    }
    _getClientY(e) {
        return e.clientY;
    }
    _clearObservable() {
        this._onTouchObservable?.clear();
        this._onTouchObservable = undefined;
    }
    _attachSource(source) {
        if (source && this._onTouchObservable) {
            source.onPointerDownObservable.add(this._onStart);
            source.onPointerMoveObservable.add(this._onMove);
            source.onPointerUpObservable.add(this._onEnd);
            source.onPointerCancelObservable.add(this._onEnd);
        }
    }
    _detachSource(source) {
        if (source) {
            source.onPointerDownObservable.removeCallback(this._onStart);
            source.onPointerMoveObservable.removeCallback(this._onMove);
            source.onPointerUpObservable.removeCallback(this._onEnd);
            source.onPointerCancelObservable.removeCallback(this._onEnd);
        }
    }
    _distance(a, b) {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        return Math.sqrt(dx * dx + dy * dy);
    }
    _angle(a, b) {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        return Math.atan2(dy, dx) * (180 / Math.PI);
    }
    _midpoint(a, b) {
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
    _detectActiveTypes(angleRad, distDelta, centerDelta) {
        const result = [];
        const angleDeg = Math.abs(angleRad * 57.2958);
        const pinchScore = Math.abs(distDelta);
        const dragScore = Math.abs(centerDelta);
        if (angleDeg > GestureDetectionSettings[TouchGestureType.Rotate].threshold) {
            result.push(TouchGestureType.Rotate);
        }
        if (pinchScore > GestureDetectionSettings[TouchGestureType.Pinch].threshold) {
            result.push(TouchGestureType.Pinch);
        }
        if (dragScore > GestureDetectionSettings[TouchGestureType.Drag].threshold) {
            result.push(TouchGestureType.Drag);
        }
        return result;
    }
}
//# sourceMappingURL=map.inputs.controller.touch.js.map